import { useState, useEffect, type MouseEvent } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import Text from '@/components/forms/text';
import SmallSpinner from '@/components/shared/spinners/small-spinner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Phone, CheckCircle, AlertTriangle } from 'lucide-react';
import type { DeclarationSinistreValues } from '@/hooks/api/use-accidents';
import type { AmendmentForm, ChangeContactVariables } from '@/hooks/api/use-amendments';
import type { Souscripteur } from '@/types/api/contract';
import type { DeclarationChangeEvent, DeclarationErrors } from '@/pages/accident/declaration-sinistre/types';

interface ClientContactVerificationProps {
    souscripteur?: Souscripteur & { phone?: string; belair_id?: unknown };
    postAmendmentChangeContact: (variables: ChangeContactVariables<AmendmentForm>) => void;
    loading?: boolean;
    values: DeclarationSinistreValues;
    setValue: UseFormSetValue<DeclarationSinistreValues>;
    handleChange: (e: DeclarationChangeEvent) => void;
    handleBlur: (e: DeclarationChangeEvent) => void;
    errors: DeclarationErrors;
}

const ClientContactVerification = (props: ClientContactVerificationProps) => {
    const {
        souscripteur,
        postAmendmentChangeContact,
        loading,
        values,
        setValue,
        handleChange,
        handleBlur,
        errors
    } = props;

    const [isValidated, setIsValidated] = useState(false);
    const [hasAttemptedValidation, setHasAttemptedValidation] = useState(false);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (souscripteur && !initialized) {
            if (!values.client_email && souscripteur.email) {
                setValue('client_email', souscripteur.email);
            }
            if (!values.client_phone && souscripteur.phone) {
                setValue('client_phone', souscripteur.phone);
            }
            setInitialized(true);
        }
    }, [souscripteur, initialized, values.client_email, values.client_phone, setValue]);

    if (!souscripteur || !souscripteur.email) {
        return null;
    }

    const handleValidateContact = (e: MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setHasAttemptedValidation(true);

        if (!values.client_email || !values.client_phone) return;
        if (errors.client_email || errors.client_phone) return;

        const hasChanges =
            values.client_email !== souscripteur.email ||
            values.client_phone !== (souscripteur.phone || '');

        if (hasChanges) {
            if (!values.client_password) return;
            if (errors.client_password) return;

            const tiers = (souscripteur.belair_id || souscripteur.id) as string | number;

            postAmendmentChangeContact({
                tiersId: tiers,
                form: {
                    tiers,
                    email: values.client_email,
                    email_confirmation: values.client_email,
                    tel_mobile: values.client_phone,
                    password: values.client_password
                },
                successFunction: () => {
                    setIsValidated(true);
                    setValue('client_contact_validated', true);
                }
            });
        } else {
            setIsValidated(true);
            setValue('client_contact_validated', true);
        }
    };

    const hasChanges =
        values.client_email !== souscripteur.email ||
        values.client_phone !== (souscripteur.phone || '');

    return (
        <Card className="mt-6">
            <CardHeader className="pb-6 border-b">
                <CardTitle className="text-lg flex items-center gap-2">
                    <Phone className="w-5 h-5 text-primary" />
                    Vos coordonnées
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-8">
                <p className="text-sm text-muted-foreground">
                    Merci de vérifier votre numéro de téléphone et votre adresse e-mail avant de valider la déclaration. Ces coordonnées nous permettent de vous informer de l'avancement de votre sinistre.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Text
                        type="tel"
                        name="client_phone"
                        autoComplete="tel"
                        label="Téléphone"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.client_phone || ''}
                        error={hasAttemptedValidation && errors.client_phone}
                        disabled={isValidated}
                    />
                    <Text
                        type="email"
                        name="client_email"
                        autoComplete="email"
                        label="Adresse e-mail"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.client_email || ''}
                        error={hasAttemptedValidation && errors.client_email}
                        disabled={isValidated}
                    />
                </div>

                {hasChanges && !isValidated && (
                    <div className="max-w-sm">
                        <Text
                            type="password"
                            name="client_password"
                            label="Votre mot de passe"
                            placeholder="Mot de passe"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.client_password || ''}
                            error={hasAttemptedValidation && errors.client_password}
                            required
                        />
                        <p className="text-xs text-muted-foreground italic mt-1">
                            Vous devez saisir votre mot de passe pour confirmer la modification de vos coordonnées.
                        </p>
                    </div>
                )}

                <div className="flex justify-end">
                    {loading ? (
                        <SmallSpinner />
                    ) : isValidated ? (
                        <Alert variant="success" className="mb-0">
                            <CheckCircle className="h-4 w-4" />
                            <AlertDescription>Coordonnées validées</AlertDescription>
                        </Alert>
                    ) : (
                        <Button onClick={handleValidateContact} className="text-white cursor-pointer">
                            Je valide mes coordonnées
                        </Button>
                    )}
                </div>

                {!isValidated && hasAttemptedValidation && (
                    <Alert variant="warning">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            Veuillez compléter vos coordonnées pour continuer.
                        </AlertDescription>
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
};

export default ClientContactVerification;
